import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

import { NumberHelper } from '../../util/number-helper';
import { generateUUID, joinClassNames } from '../../util/util';
import { useChatLogin } from '../../contexts/ChatLoginContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { Close } from '../QPlayerSVGElement';

import './ChatLoginOverlay.scss';

interface ChatLoginOverlayProps {
	settings?: any;
	isMobile?: boolean;
}
const baseClassname = 'chatLoginForm';

const ChatLoginOverlay = ({ settings, isMobile }: ChatLoginOverlayProps) => {
	const { t } = useTranslation();

	const { username, errorMessage, show, requireUsernameLogin, userId: userIdProps, setErrorMessage, setUsername, setShowLoginForm, setUserId } = useChatLogin();
	const { isChatActivated, accountId, configParams } = useConfiguration();

	const [userNameInput, setUserNameInput] = useState<string | undefined>(undefined);

	const anonymousChatName = useMemo(() => `user#${NumberHelper.getRandomInt(0, 9999)}`, []);

	const isIgnoreChatCookies = () => {
		if (configParams.ignoreChatCookies) {
			return configParams.ignoreChatCookies === 'true';
		}

		return settings?.additionalSettings?.noChatCookies === true;
	};

	const rememberChatUserInfo = ({ userName, userId, userEmail }: { userName?: string; userId?: string; userEmail?: string }) => {
		if (isIgnoreChatCookies()) {
			return;
		}

		userId && Cookies.set(`chat_ID_${accountId}`, userId);
		userName && Cookies.set(`chat_name_${accountId}`, userName);
		userEmail && Cookies.set(`chat_email_${accountId}`, userEmail);
	};

	const handleJoinWithUsernameButton = () => {
		const userId = show === 'edit' ? userIdProps : generateUUID();
		const chatName = userNameInput === undefined ? anonymousChatName : userNameInput;
		if (chatName.trim() === username) {
			setShowLoginForm(false);
			return;
		}

		if (requireUsernameLogin === true) {
			if (!chatName) {
				setErrorMessage(t('USERNAME_REQUIRED'));
			} else {
				setUsername(chatName);
				show !== 'edit' && setUserId(userId);
				setShowLoginForm(false);
				rememberChatUserInfo({
					userName: chatName,
					userId: show === 'edit' ? undefined : userId,
				});
			}
		} else {
			setUsername(chatName);
			show !== 'edit' && setUserId(userId);
			setShowLoginForm(false);
			rememberChatUserInfo({
				userName: chatName,
				userId: show === 'edit' ? undefined : userId,
			});
		}
	};

	const handleCancelButton = () => {
		setShowLoginForm(false);
		setErrorMessage('');
	};

	const stopEvent = useCallback((e: React.TouchEvent) => {
		e.stopPropagation();
		e.preventDefault();
	}, []);

	useEffect(() => {
		const cookieUserName = Cookies.get(`chat_name_${accountId}`);
		setUserNameInput(cookieUserName ? cookieUserName : undefined);
	}, [show]);

	return show ? (
		<div
			className={joinClassNames([`${baseClassname}Wrapper`, isMobile ? `${baseClassname}Wrapper--mobile` : ''])}
			onTouchMove={stopEvent}
			onTouchStart={stopEvent}
		>
			<div className={`${baseClassname}Container`}>
				<div className={`${baseClassname}Title`}>
					<h3 style={{ fontSize: "1em" }}>{show === 'edit' ? t('EDIT_THE_CHAT') : isChatActivated ? t('JOIN_THE_CHAT') : t('JOIN_QNA')}</h3>
					<button className={`${baseClassname}CloseButton`} onClick={handleCancelButton}>
						{isMobile ? 'X' : <Close color={isMobile ? '#000' : undefined} />}
					</button>
				</div>

				<label className={`${baseClassname}Label`}>{t('CHAT_USERNAME')}</label>
				<input
					className={`${baseClassname}Input`}
					type="text"
					maxLength={300}
					placeholder={t('CHAT_USERNAME_PLACEHOLDER')}
					value={userNameInput ?? anonymousChatName}
					onChange={(e) => setUserNameInput(e.target.value)}
					style={userNameInput === undefined ? { opacity: '0.6', fontSize: "0.875em" } : { fontSize: "0.875em" }}
					onFocus={() => show !== 'edit' && !username && userNameInput === undefined && setUserNameInput('')}
					onBlur={() => show !== 'edit' && !userNameInput && !username && setUserNameInput(undefined)}
				/>

				<div className={`${baseClassname}ActionButtonJoinError`}>{errorMessage}</div>

				<div className={`${baseClassname}ActionButtonWrapper`}>
					<div
						className={`${baseClassname}ActionButtonJoin`}
						style={{ backgroundColor: settings?.playerStyling?.primaryColor ?? '#126cfc' }}
						onClick={handleJoinWithUsernameButton}
					>
						<span>{show === 'edit' ? t('LABEL_EDIT_CHAT') : t('LABEL_JOIN_CHAT')}</span>
					</div>
				</div>
			</div>
		</div>
	) : null;
};

export default ChatLoginOverlay;
